import type {
	Collection,
	CollectionDefaults,
	FindOptions,
	ResolvedCollection,
	StoreCore,
	StoreSchema,
} from '@rstore/shared';
import type { CrudActionType as CrudActionName } from '#layers/access/public';
import type { FieldSelection, Filter, PrimitivePrimaryKey, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { CrudAction } from '#layers/access/public';
import { isNil, stringifyQuery } from '#layers/base/public';
import { hasIncludeFilter, isNonEmptyFilter, isRelationalFilter, mergeAnd, normalizeQueryForBackend } from '#layers/data-model/public';
import { apiEndpoint } from './api-endpoint';
import { getSelection } from './get-selection';
import { resolveSchemaCollection } from './resolve-schema-collection';

interface GetFetchOptions {
	store: StoreCore<StoreSchema, CollectionDefaults>;
	collection: ResolvedCollection;
	findOptions?: FindOptions<Collection, CollectionDefaults, StoreSchema>;
	key?: PrimitivePrimaryKey;
	selection?: FieldSelection;
	filter?: Filter;
	operation: CrudActionName;
}

/**
 * Combines endpoint, fields, filters, pagination, and custom params into the API request shape.
 *
 * Structured filters are normalized before query-string serialization so canonical in-memory
 * operands, such as Temporal date/time values, become backend wire values at one boundary.
 */
export function getFetchOptions(options: GetFetchOptions) {
	const { store, collection, findOptions, key, filter, operation } = options;

	const endpoint = apiEndpoint({ collection, key, operation });
	const mutationFields = operation !== CrudAction.Read ? collection.meta?.mutationFields : undefined;
	const rawSelection = options.selection ?? (mutationFields ? { fields: mutationFields } : getSelection({ store, collection, findOptions }));

	let finalFilter: Filter | undefined;

	if (isNonEmptyFilter(filter) && isNonEmptyFilter(findOptions?.filter)) {
		finalFilter = mergeAnd(filter, findOptions.filter);
	}
	else if (isNonEmptyFilter(filter)) {
		finalFilter = filter;
	}
	else if (isNonEmptyFilter(findOptions?.filter)) {
		finalFilter = findOptions.filter;
	}

	const include = rawSelection.include;
	const hasNestedFilter = include ? hasIncludeFilter(include) : false;
	const needsSchemaCollection = hasNestedFilter || isRelationalFilter(finalFilter);
	const schemaCollection = needsSchemaCollection
		? resolveSchemaCollection({ collectionName: collection.name, store })
		: undefined;
	const primitiveFieldTypes = collection.meta?.primitiveFieldTypes;
	const normalizationContext = {
		primitiveFieldTypes,
		schemaCollection,
		getPrimitiveFieldTypes: (schemaCollection: ResolvedMonospaceCollection) => {
			const collection = store.$collections.find((collection) => collection.name === schemaCollection.apiName);

			return collection?.meta?.primitiveFieldTypes;
		},
	};

	const normalizedQuery = normalizeQueryForBackend({
		...rawSelection,
		filter: finalFilter,
		sort: findOptions?.sort,
		limit: findOptions?.limit,
		offset: findOptions?.offset,
	}, normalizationContext);

	const { pageSize, pageIndex } = findOptions ?? {};
	const hasPage = !isNil(pageIndex);
	const hasPageSize = !isNil(pageSize);
	const paginated = hasPage && hasPageSize;

	if (hasPage && !hasPageSize) {
		console.error('findOptions has pageIndex but no pageSize', findOptions);
	}

	// Simple pagination = previous/next: load one extra item to detect a next page.
	// TODO handle full pagination (shouldn't need to add an extra item)
	const pagination = paginated
		? {
				limit: pageSize + 1,
				offset: pageIndex * pageSize,
				meta: findOptions?.includeTotalCount === false ? undefined : { totalCount: true },
			}
		: {};

	const query = stringifyQuery({
		...normalizedQuery,
		...findOptions?.params,
		...pagination,
	});

	// Sanity checks
	if (findOptions?.fetchPolicy !== 'no-cache' && isRelationalFilter(filter)) {
		console.warn(`The following query contains a relational filter that can not be safely evaluated on the client: ${endpoint}?${query}`, filter);
	}

	return {
		endpoint,
		query,
		paginated,
	};
}
